import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  MessageCircle,
  ShieldCheck,
  Clock,
  ArrowRight,
  CheckCircle2,
  Calendar,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/AnimatedSection";
import { Button } from "../components/Button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { SITE } from "../data/site";

// ==========================================
// LEAD API URL
// ==========================================

const API_URL = "https://socialwaveztechnologies.com/lead-api";

// ==========================================
// FORM OPTIONS
// ==========================================

const NEEDS = [
  "Website",
  "SaaS / Software",
  "CRM",
  "AI / Automation",
  "SEO",
  "Multiple / Connected System",
  "Not Sure",
];

const BUDGETS = [
  "Under ₹50,000",
  "₹50,000 – ₹1.5L",
  "₹1.5L – ₹5L",
  "₹5L+",
  "Ongoing monthly engagement",
  "Not sure yet",
];

const Contact = () => {
  const [step, setStep] = useState("form");

  const [needs, setNeeds] = useState([]);
  const [budget, setBudget] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // ==========================================
  // SEO
  // ==========================================

  useSEO(
    "Contact Us | Social Wavez Technologies",
    "Tell us what you're trying to build, automate, or connect — we'll map the right system on a free 20-minute discovery call."
  );

  // ==========================================
  // AUTO OPEN CALENDLY AFTER 3 SECONDS
  // ==========================================

  useEffect(() => {
    let timer;

    if (step === "success") {
      timer = setTimeout(() => {
        setStep("calendar");
      }, 3000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [step]);

  // ==========================================
  // TOGGLE NEEDS
  // ==========================================

  const toggleNeed = (need) => {
    setNeeds((prev) =>
      prev.includes(need)
        ? prev.filter((item) => item !== need)
        : [...prev, need]
    );
  };

  // ==========================================
  // SUBMIT FORM
  // ==========================================

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validation

    if (!name.trim()) {
      toast.error("Please enter your name.");
      return;
    }

    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }

    if (!phone.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }

    if (!location.trim()) {
      toast.error("Please enter your location.");
      return;
    }

    if (needs.length === 0) {
      toast.error("Please select what you need.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/leads`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          name: name.trim(),

          email: email.trim(),

          phone: phone.trim(),

          location: location.trim(),

          requirement: needs.join(", "),

          package: company.trim() || undefined,

          budget: budget || "Not specified",

          company: company.trim() || undefined,

          message: message.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || "Failed to submit your details."
        );
      }

      // Success

      setStep("success");

      toast.success(
        "Details received! Opening the booking calendar..."
      );
    } catch (error) {
      console.error("Lead submission error:", error);

      toast.error(
        error.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ==========================================
  // CALENDLY URL
  // ==========================================

  const calendlySrc = SITE.calendlyUrl
    ? `${SITE.calendlyUrl}?hide_gdpr_banner=1&name=${encodeURIComponent(
        name
      )}&email=${encodeURIComponent(email)}`
    : null;

  return (
    <>
      {/* ========================================== */}
      {/* HERO */}
      {/* ========================================== */}

      <section className="bg-deep relative overflow-hidden pb-20 pt-40 md:pb-24 md:pt-52">
        <div className="bg-grid-dark absolute inset-0 opacity-50" />

        <div className="animate-float-slow pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#B8912A]/15 blur-[130px]" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
              Contact Us
            </p>
          </Reveal>

          <h1 className="mt-7 max-w-4xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLines
              trigger="mount"
              delay={0.25}
              lines={[
                "Let's Build the Right System",
                <span
                  key="ct2"
                  className="text-gradient-signature"
                >
                  for Your Business.
                </span>,
              ]}
            />
          </h1>

          <Reveal delay={0.45}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">
              Tell us what you're trying to build, automate, or connect.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ========================================== */}
      {/* CONTACT SECTION */}
      {/* ========================================== */}

      <section className="bg-[#F8FBF6] py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 md:px-12 lg:grid-cols-[0.9fr_1.1fr]">

          {/* ========================================== */}
          {/* LEFT SIDE */}
          {/* ========================================== */}

          <div>
            <Reveal>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#07130C] md:text-3xl">
                Start with a conversation, not a quote.
              </h2>

              <p className="mt-4 text-base leading-relaxed text-[#07130C]/60">
                The first call is free, 20 minutes, and genuinely useful — we
                map your business problem to a system, even if you don't build
                it with us.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <ul className="mt-10 space-y-5">
                {[
                  {
                    icon: Mail,
                    label: "Email",
                    value: SITE.email,
                    href: `mailto:${SITE.email}`,
                    testid: "contact-email",
                  },
                  {
                    icon: Phone,
                    label: "Phone",
                    value: SITE.phone,
                    href: `tel:${SITE.phone.replace(/\s/g, "")}`,
                    testid: "contact-phone",
                  },
                  {
                    icon: MessageCircle,
                    label: "WhatsApp",
                    value: "Chat with the team",
                    href: SITE.whatsapp,
                    testid: "contact-whatsapp",
                  },
                ].map((c) => (
                  <li key={c.label}>
                    <a
                      href={c.href}
                      data-testid={c.testid}
                      className="group flex items-center gap-4"
                      target={
                        c.label === "WhatsApp"
                          ? "_blank"
                          : undefined
                      }
                      rel="noopener noreferrer"
                    >
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1C7543]/12 to-[#D3B74D]/12 text-[#1C7543] transition-transform duration-300 group-hover:scale-110">
                        <c.icon className="h-5 w-5" />
                      </span>

                      <span>
                        <span className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-[#07130C]/40">
                          {c.label}
                        </span>

                        <span className="block text-sm font-semibold text-[#07130C] transition-colors group-hover:text-[#1C7543]">
                          {c.value}
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.2}>
              <div
                className="mt-12 space-y-3 rounded-3xl border border-[#07130C]/10 bg-white p-7"
                data-testid="contact-trust-strip"
              >
                <p className="flex items-center gap-3 text-sm font-medium text-[#07130C]/70">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-[#1C7543]" />
                  No spam. No hard sell. Just a clear plan for your system.
                </p>

                <p className="flex items-center gap-3 text-sm font-medium text-[#07130C]/70">
                  <Clock className="h-5 w-5 shrink-0 text-[#1C7543]" />
                  We typically respond within 24 hours.
                </p>
              </div>
            </Reveal>
          </div>

          {/* ========================================== */}
          {/* FORM */}
          {/* ========================================== */}

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-[#07130C]/10 bg-white p-8 shadow-[0_30px_80px_rgba(14,58,40,0.12)] md:p-10">

              <AnimatePresence mode="wait">

                {/* ========================================== */}
                {/* FORM */}
                {/* ========================================== */}

                {step === "form" && (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -14 }}
                    onSubmit={onSubmit}
                    className="grid gap-5 sm:grid-cols-2"
                    data-testid="contact-form"
                  >
                    {/* NAME */}

                    <div>
                      <Label htmlFor="ct-name">
                        Name *
                      </Label>

                      <Input
                        id="ct-name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="mt-2"
                      />
                    </div>

                    {/* EMAIL */}

                    <div>
                      <Label htmlFor="ct-email">
                        Email *
                      </Label>

                      <Input
                        id="ct-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="mt-2"
                      />
                    </div>

                    {/* PHONE */}

                    <div>
                      <Label htmlFor="ct-phone">
                        Phone *
                      </Label>

                      <Input
                        id="ct-phone"
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 ..."
                        className="mt-2"
                      />
                    </div>

                    {/* LOCATION */}

                    <div>
                      <Label htmlFor="ct-location">
                        Location *
                      </Label>

                      <Input
                        id="ct-location"
                        required
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="City, Country"
                        className="mt-2"
                      />
                    </div>

                    {/* COMPANY */}

                    <div className="sm:col-span-2">
                      <Label htmlFor="ct-company">
                        Company
                      </Label>

                      <Input
                        id="ct-company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Company name"
                        className="mt-2"
                      />
                    </div>

                    {/* NEEDS */}

                    <div className="sm:col-span-2">
                      <Label>
                        What do you need? *
                      </Label>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {NEEDS.map((n) => (
                          <button
                            key={n}
                            type="button"
                            aria-pressed={needs.includes(n)}
                            onClick={() => toggleNeed(n)}
                            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                              needs.includes(n)
                                ? "border-transparent bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white"
                                : "border-[#07130C]/15 text-[#07130C]/60 hover:border-[#1C7543]/40 hover:text-[#07130C]"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* BUDGET */}

                    <div className="sm:col-span-2">
                      <Label>
                        Budget / Project Range
                      </Label>

                      <Select
                        value={budget}
                        onValueChange={setBudget}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a range (optional)" />
                        </SelectTrigger>

                        <SelectContent>
                          {BUDGETS.map((b) => (
                            <SelectItem
                              key={b}
                              value={b}
                            >
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* MESSAGE */}

                    <div className="sm:col-span-2">
                      <Label htmlFor="ct-msg">
                        Message
                      </Label>

                      <Textarea
                        id="ct-msg"
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="What's manual, disconnected or broken right now?"
                        className="mt-2"
                      />
                    </div>

                    {/* SUBMIT */}

                    <div className="sm:col-span-2">
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full justify-center"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Continue — Book My Free Discovery Call
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.form>
                )}

                {/* ========================================== */}
                {/* SUCCESS */}
                {/* ========================================== */}

                {step === "success" && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.45,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="flex flex-col items-center py-8 text-center"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 220,
                        damping: 14,
                        delay: 0.1,
                      }}
                      className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#3CBE6E] to-[#D3B74D] text-white"
                    >
                      <CheckCircle2 className="h-9 w-9" />
                    </motion.span>

                    <h3 className="mt-7 font-display text-2xl font-semibold text-[#07130C]">
                      Got it{name ? `, ${name.split(" ")[0]}` : ""}.
                    </h3>

                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#07130C]/55">
                      Your details have been received successfully.
                    </p>

                    <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[#1C7543]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Opening the booking calendar in 3 seconds...
                    </div>
                  </motion.div>
                )}

                {/* ========================================== */}
                {/* CALENDAR */}
                {/* ========================================== */}

                {step === "calendar" && (
                  <motion.div
                    key="calendar"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.45,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    {calendlySrc ? (
                      <iframe
                        src={calendlySrc}
                        title="Book a free discovery call"
                        className="h-[620px] w-full rounded-2xl border border-[#07130C]/10"
                      />
                    ) : (
                      <div className="flex h-[420px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#1C7543]/35 bg-[#1C7543]/5 p-8 text-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#1C7543] to-[#D3B74D] text-white">
                          <Calendar className="h-7 w-7" />
                        </span>

                        <h3 className="mt-6 font-display text-xl font-semibold text-[#07130C]">
                          Calendly booking goes here
                        </h3>

                        <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#07130C]/55">
                          Add your Calendly URL in{" "}
                          <code className="rounded bg-[#07130C]/5 px-1.5 py-0.5 text-xs">
                            src/data/site.js
                          </code>
                        </p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => setStep("form")}
                      className="mt-5 text-sm font-semibold text-[#1C7543] hover:text-[#D3B74D]"
                    >
                      ← Edit my details
                    </button>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;